/** Runtime configuration. Read this, never import.meta.env, anywhere else in the app. */

import type { Environment } from "@nebulize/sdk";

import { endpointDefaults, resolveEnvironment } from "./environment";

export const environment: Environment = resolveEnvironment(
  import.meta.env.VITE_NEBULIZE_ENV,
  import.meta.env.MODE,
);

const ABSOLUTE = /^[a-z][a-z0-9+.-]*:\/\//i;

function trimTrailingSlash(value: string): string {
  return value.endsWith("/") ? value.slice(0, -1) : value;
}

function originFor(scheme: "http" | "ws"): string {
  const { protocol, host } = window.location;
  const secure = protocol === "https:";
  const resolved =
    scheme === "ws" ? (secure ? "wss" : "ws") : secure ? "https" : "http";
  return `${resolved}://${host}`;
}

/** Always yields an absolute URL, so nothing silently relies on the dev-server proxy. */
function resolveBaseUrl(
  configured: string | undefined,
  fallbackPath: string,
  scheme: "http" | "ws",
): string {
  const value = configured?.trim() ?? "";
  if (value.length > 0) {
    if (!ABSOLUTE.test(value)) {
      throw new Error(
        `Expected an absolute URL, received ${JSON.stringify(value)}`,
      );
    }
    return trimTrailingSlash(value);
  }
  return `${originFor(scheme)}${fallbackPath}`;
}

const defaults = endpointDefaults(environment);

export const apiBaseUrl = resolveBaseUrl(
  import.meta.env.VITE_API_BASE_URL,
  defaults.apiPath,
  "http",
);

export const wsBaseUrl = resolveBaseUrl(
  import.meta.env.VITE_WS_BASE_URL,
  defaults.wsPath,
  "ws",
);

const DEFAULT_AGENT_PORT = 47821;

/**
 * The desktop agent's loopback api, which is a different machine's worth of state from the api.
 *
 * The dev server proxies it under `/agent-api`, because there the page and the agent are the same
 * origin and a proxy is the cheapest way to keep the agent's `/status` from colliding with the
 * `/agent` routes. Every other deployment reaches it directly at loopback: the page is served from
 * `https://nebulize.app` and the agent runs on the reader's own machine, so there is nothing in
 * between to proxy through. 127.0.0.1 is a trustworthy origin, so a secure page may call it, and
 * the agent names this site in its own CORS allowlist.
 *
 * The port is `local_port` in the agent's `core/settings.py`. Two spellings of one number, and the
 * agent is the one that owns it.
 */
export const agentBaseUrl: string = resolveAgentBaseUrl();

/**
 * What a person checks when a screen says the agent is not answering.
 *
 * The loopback address itself, never `agentBaseUrl`: in a checkout that resolves to the dev
 * server's proxy path, and telling somebody their agent is not answering on `/agent-api` names a
 * thing they cannot look at.
 */
export const agentDisplayAddress = `127.0.0.1:${String(DEFAULT_AGENT_PORT)}`;

function resolveAgentBaseUrl(): string {
  const configured = import.meta.env.VITE_AGENT_BASE_URL?.trim() ?? "";
  if (configured.length > 0) {
    if (!ABSOLUTE.test(configured)) {
      throw new Error(
        `Expected an absolute URL, received ${JSON.stringify(configured)}`,
      );
    }
    return trimTrailingSlash(configured);
  }
  return defaults.usesDevProxy
    ? "/agent-api"
    : `http://127.0.0.1:${DEFAULT_AGENT_PORT}`;
}

/** Where this deployment lives, taken from the page it is being served from. */
export const siteBaseUrl: string = trimTrailingSlash(
  import.meta.env.VITE_SITE_BASE_URL?.trim() || window.location.origin,
);

/** The address a host hands out for a space. It carries no credential and never changes. */
export function spaceAddress(joinCode: string): string {
  return `${siteBaseUrl}/${joinCode}`;
}

/** One canvas of a space, named by the role that reaches it. */
export function canvasAddress(joinCode: string, role: string): string {
  return `${spaceAddress(joinCode)}/${role}`;
}

/** The address a device is pasted once. It names a canvas kind, never an account or a space. */
export function deviceCanvasAddress(kind: string): string {
  return `${siteBaseUrl}/canvas/${kind}`;
}

/** The one address pasted into OBS. There is a single broadcast canvas and this is it. */
export function broadcastAddress(): string {
  return deviceCanvasAddress("broadcast");
}

/** The account's own durable address, which survives every space change. */
export function accountAddress(handle: string): string {
  return `${siteBaseUrl}/${handle}`;
}
